/**
 * The superadmin sidebar is generated from the permission matrix.
 *
 * A role with no grant on a module never sees the menu entry, and typing the URL
 * lands on access-refused — the router refuses it server-side too. Keeping the
 * mapping here, free of React, means it can be tested against the module list the
 * backend actually publishes.
 */

// Nav item id (or id prefix) → the platform module that governs it.
// Longest prefix wins, exactly like the backend's path map.
export const NAV_MODULE = {
  overview: 'tenant_directory',
  tenants: 'tenant_directory',
  users: 'tenant_directory',
  // Tenant Cloud mixes concerns under one group, so its sub-items are mapped
  // individually — otherwise its Billing tab would ride in on provisioning.
  'control/create-tenant': 'provisioning',
  'control/billing': 'subscription_plans',
  'control/access': 'roles_permissions',
  'control/governance': 'master_data',
  control: 'provisioning',
  billing: 'subscription_plans',
  'access-management': 'subscription_plans',
  plans: 'subscription_plans',
  marketplace: 'vertical_packs',
  'event-bus': 'integration_health',
  master: 'master_data',
  'platform-core': 'platform_core',
  system: 'system_settings',
  'access-control': 'roles_permissions',
  audit: 'impersonation_log',
  'ai-command': 'platform_core',
  'ai-center': 'platform_core',
};

// Always reachable: a person's own profile is not a platform module, and locking
// someone out of it would leave them signed in with nowhere to go.
export const ALWAYS_VISIBLE = new Set(['profile']);

export const moduleForNavId = (id) => {
  if (!id) return null;
  const candidates = Object.keys(NAV_MODULE)
    .filter((key) => id === key || id.startsWith(`${key}/`))
    .sort((a, b) => b.length - a.length);
  return candidates.length ? NAV_MODULE[candidates[0]] : null;
};

/**
 * Filter a nav schema down to what this operator may reach.
 *
 * `access` is the /my-platform-access payload. Until it arrives (or if it fails)
 * the schema is returned untouched — a slow request must not blank the sidebar,
 * and the server refuses anything the client wrongly shows.
 */
export const filterNavByAccess = (navItems, access) => {
  if (!access || !Array.isArray(access.modules)) return navItems;
  const granted = new Set(access.modules);

  return navItems.filter((item) => {
    if (ALWAYS_VISIBLE.has(item.id)) return true;
    const module = moduleForNavId(item.id);
    // An unmapped entry stays visible; the router is the authority, and hiding
    // something nobody mapped would silently shrink the product.
    if (!module) return true;
    return granted.has(module);
  }).map((item) => {
    if (!Array.isArray(item.subItems)) return item;
    const subItems = item.subItems.filter((sub) => {
      const module = moduleForNavId(sub.id);
      return !module || granted.has(module);
    });
    return subItems.length ? { ...item, subItems } : item;
  });
};
